import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha,
  useTheme,
  Divider,
  Select,
  FormControl,
  Tooltip
} from '@mui/material';
import { Captcha } from 'recaptz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SortIcon from '@mui/icons-material/Sort';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../util/auth';
import {
  useComments,
  addComment,
  deleteComment,
  updateComment,
  muteUser,
  unmuteUser,
  useUserMutedStatus,
  useUserData,
  voteComment,
  getUserCommentVote
} from '../util/db';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';

const BankDiscussion = ({ bankId, isAdmin }) => {
  const theme = useTheme();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [newComment, setNewComment] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [muteDialogOpen, setMuteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sortBy, setSortBy] = useState('votes');
  const [userVotes, setUserVotes] = useState({});
  const [captchaValid, setCaptchaValid] = useState(false);
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);

  const { data: comments = [], isLoading } = useComments(bankId, sortBy);
  const { data: isMuted = false } = useUserMutedStatus(auth.user?.uid);

  const darkMode = theme.palette.mode === 'dark';

  const bgColors = {
    card: darkMode ? alpha('#1E293B', 0.8) : '#FFFFFF',
    input: darkMode ? alpha('#334155', 0.6) : alpha('#F1F5F9', 0.8),
    border: darkMode ? alpha('#94A3B8', 0.1) : alpha('#94A3B8', 0.12),
    hover: darkMode ? alpha('#334155', 0.8) : alpha('#F1F5F9', 0.95),
    text: {
      primary: darkMode ? '#F8FAFC' : '#0F172A',
      secondary: darkMode ? alpha('#94A3B8', 0.8) : '#475569'
    }
  };

  const handleMenuOpen = (event, comment) => {
    setAnchorEl(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedComment(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleMuteClick = () => {
    setMuteDialogOpen(true);
    handleMenuClose();
  };

  const handleEditClick = () => {
    if (selectedComment) {
      setEditingCommentId(selectedComment.id);
      setEditedText(selectedComment.text);
    }
    handleMenuClose();
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditedText('');
  };

  const handleSaveEdit = async (commentId) => {
    if (!editedText.trim()) {
      enqueueSnackbar('Comment cannot be empty', { variant: 'warning' });
      return;
    }

    try {
      await updateComment(bankId, commentId, editedText.trim());
      queryClient.invalidateQueries(['comments', bankId]);
      enqueueSnackbar('Comment updated successfully', { variant: 'success' });
      setEditingCommentId(null);
      setEditedText('');
    } catch (error) {
      console.error('Error updating comment:', error);
      enqueueSnackbar('Failed to update comment', { variant: 'error' });
    }
  };

  const handleDeleteComment = async () => {
    if (!selectedComment || !isAdmin) return;

    try {
      await deleteComment(bankId, selectedComment.id);
      queryClient.invalidateQueries(['comments', bankId]);
      enqueueSnackbar('Comment deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting comment:', error);
      enqueueSnackbar('Failed to delete comment', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedComment(null);
    }
  };

  const handleMuteUser = async () => {
    if (!selectedComment || !isAdmin) return;

    try {
      await muteUser(selectedComment.userId, auth.user.uid);
      queryClient.invalidateQueries(['mutedStatus', selectedComment.userId]);
      enqueueSnackbar('User muted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error muting user:', error);
      enqueueSnackbar('Failed to mute user', { variant: 'error' });
    } finally {
      setMuteDialogOpen(false);
      setSelectedComment(null);
    }
  };

  const handleUnmuteUser = async (userId) => {
    if (!isAdmin) return;

    try {
      await unmuteUser(userId);
      queryClient.invalidateQueries(['mutedStatus', userId]);
      enqueueSnackbar('User unmuted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error unmuting user:', error);
      enqueueSnackbar('Failed to unmute user', { variant: 'error' });
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!auth.user) {
      enqueueSnackbar('Please sign in to comment', { variant: 'warning' });
      return;
    }

    if (isMuted) {
      enqueueSnackbar('You are muted and cannot post comments', { variant: 'error' });
      return;
    }

    if (!newComment.trim()) {
      enqueueSnackbar('Comment cannot be empty', { variant: 'warning' });
      return;
    }

    // Show CAPTCHA modal instead of direct validation
    if (!captchaValid) {
      setShowCaptchaModal(true);
      return;
    }

    // If CAPTCHA is already valid, submit directly
    await submitComment();
  };

  const submitComment = async () => {
    setIsSubmitting(true);

    try {
      await addComment(bankId, {
        userId: auth.user.uid,
        userEmail: auth.user.email,
        userName: auth.user.displayName || auth.user.email.split('@')[0],
        text: newComment.trim()
      });

      setNewComment('');
      setCaptchaValid(false); // Reset CAPTCHA after successful post
      setShowCaptchaModal(false);
      queryClient.invalidateQueries(['comments', bankId]);
      enqueueSnackbar('Comment posted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error posting comment:', error);
      enqueueSnackbar('Failed to post comment', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReplyClick = (commentId) => {
    setReplyingToId(commentId);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingToId(null);
    setReplyText('');
  };

  const handleSubmitReply = async (parentId) => {
    if (!auth.user) {
      enqueueSnackbar('Please sign in to reply', { variant: 'warning' });
      return;
    }

    if (isMuted) {
      enqueueSnackbar('You are muted and cannot post replies', { variant: 'error' });
      return;
    }

    if (!replyText.trim()) {
      enqueueSnackbar('Reply cannot be empty', { variant: 'warning' });
      return;
    }

    try {
      await addComment(bankId, {
        userId: auth.user.uid,
        userEmail: auth.user.email,
        userName: auth.user.displayName || auth.user.email.split('@')[0],
        text: replyText.trim(),
        parentId
      });

      setReplyingToId(null);
      setReplyText('');
      queryClient.invalidateQueries(['comments', bankId, sortBy]);
      enqueueSnackbar('Reply posted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error posting reply:', error);
      enqueueSnackbar('Failed to post reply', { variant: 'error' });
    }
  };

  // Fetch user votes for all comments
  useEffect(() => {
    const fetchUserVotes = async () => {
      if (!auth.user || !comments.length) return;

      const votes = {};
      const fetchVotes = async (commentList) => {
        for (const comment of commentList) {
          const vote = await getUserCommentVote(bankId, comment.id, auth.user.uid);
          if (vote) votes[comment.id] = vote;

          // Also fetch votes for replies
          if (comment.replies && comment.replies.length > 0) {
            await fetchVotes(comment.replies);
          }
        }
      };

      await fetchVotes(comments);
      setUserVotes(votes);
    };

    fetchUserVotes();
  }, [comments, auth.user, bankId]);

  const handleVote = async (commentId, voteType) => {
    if (!auth.user) {
      enqueueSnackbar('Please sign in to vote', { variant: 'warning' });
      return;
    }

    try {
      await voteComment(bankId, commentId, auth.user.uid, voteType);

      // Update local vote state
      const currentVote = userVotes[commentId];
      if (currentVote === voteType) {
        // Remove vote
        const newVotes = { ...userVotes };
        delete newVotes[commentId];
        setUserVotes(newVotes);
      } else {
        // Add or change vote
        setUserVotes({ ...userVotes, [commentId]: voteType });
      }

      queryClient.invalidateQueries(['comments', bankId, sortBy]);
    } catch (error) {
      console.error('Error voting:', error);
      enqueueSnackbar('Failed to vote', { variant: 'error' });
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Recently';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: bgColors.card,
        border: `1px solid ${bgColors.border}`,
        borderRadius: 2,
      }}
    >
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ChatBubbleOutlineIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: bgColors.text.primary }}>
              Discussion
            </Typography>
            <Chip
              label={comments.length}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 600,
                height: 24,
              }}
            />
          </Box>

          {/* Sort Controls */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{
                bgcolor: bgColors.input,
                borderRadius: 1.5,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: bgColors.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiSelect-select': {
                  color: bgColors.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                },
              }}
              startAdornment={<SortIcon sx={{ fontSize: 18, color: bgColors.text.secondary, mr: 0.5 }} />}
            >
              <MenuItem value="votes">Most Votes</MenuItem>
              <MenuItem value="recent">Most Recent</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ borderColor: bgColors.border }} />

        {/* Comment Input */}
        {auth.user && (
          <Box component="form" onSubmit={handleSubmitComment}>
            <Stack spacing={2}>
              {isMuted && (
                <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                  You have been muted and cannot post comments. Please contact an administrator.
                </Alert>
              )}
              {/* Integrated Comment Input with Button */}
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${bgColors.border}`,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  bgcolor: bgColors.input,
                  transition: 'border-color 0.2s',
                  '&:focus-within': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={isMuted ? "You are muted" : "Share your experience or ask a question..."}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  disabled={isMuted || isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: bgColors.text.primary,
                    },
                  }}
                />

                <Divider sx={{ borderColor: bgColors.border }} />

                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  p: 1.5,
                  bgcolor: alpha(bgColors.input, 0.5),
                }}>
                  <Button
                    type="submit"
                    variant="contained"
                    endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    disabled={isMuted || isSubmitting || !newComment.trim()}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    {isSubmitting ? 'Posting...' : 'Post Comment'}
                  </Button>
                </Box>
              </Paper>
            </Stack>
          </Box>
        )}

        {!auth.user && (
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            Please sign in to join the discussion
          </Alert>
        )}

        {/* CAPTCHA Modal */}
        <Dialog
          open={showCaptchaModal}
          onClose={() => {
            setShowCaptchaModal(false);
            setCaptchaValid(false);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              bgcolor: bgColors.card,
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 600,
            color: bgColors.text.primary,
          }}>
            Verify You're Human
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{
              mb: 3,
              color: bgColors.text.secondary,
            }}>
              Please complete the CAPTCHA verification to post your comment.
            </DialogContentText>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}>
              <Captcha
                type="mixed"
                length={6}
                darkMode={darkMode}
                showSuccessAnimation
                caseSensitive={false}
                onValidate={(isValid) => {
                  setCaptchaValid(isValid);
                  if (isValid) {
                    // Auto-submit after successful CAPTCHA with slight delay for animation
                    setTimeout(() => {
                      setShowCaptchaModal(false);
                      submitComment();
                    }, 800);
                  }
                }}
                validationRules={{
                  required: true,
                  minLength: 6,
                  maxLength: 6
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={() => {
                setShowCaptchaModal(false);
                setCaptchaValid(false);
              }}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Comments List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : comments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: bgColors.text.secondary, opacity: 0.3, mb: 2 }} />
            <Typography variant="body1" sx={{ color: bgColors.text.secondary }}>
              No comments yet. Be the first to share your thoughts!
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                isAdmin={isAdmin}
                currentUserId={auth.user?.uid}
                onMenuOpen={handleMenuOpen}
                onUnmute={handleUnmuteUser}
                bgColors={bgColors}
                formatTimestamp={formatTimestamp}
                getInitials={getInitials}
                theme={theme}
                editingCommentId={editingCommentId}
                editedText={editedText}
                setEditedText={setEditedText}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                replyingToId={replyingToId}
                replyText={replyText}
                setReplyText={setReplyText}
                onReplyClick={handleReplyClick}
                onSubmitReply={handleSubmitReply}
                onCancelReply={handleCancelReply}
                isMuted={isMuted}
                onVote={handleVote}
                userVotes={userVotes}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {/* Comment Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {isAdmin && (
          <MenuItem onClick={handleEditClick}>
            <EditIcon sx={{ mr: 1, fontSize: 20 }} />
            Edit Comment
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
            Delete Comment
          </MenuItem>
        )}
        {isAdmin && selectedComment?.userId !== auth.user?.uid && (
          <MenuItem onClick={handleMuteClick}>
            <BlockIcon sx={{ mr: 1, fontSize: 20 }} />
            Mute User
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Comment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this comment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteComment} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mute Confirmation Dialog */}
      <Dialog open={muteDialogOpen} onClose={() => setMuteDialogOpen(false)}>
        <DialogTitle>Mute User?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mute this user? They will not be able to post any comments.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMuteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleMuteUser} color="error" variant="contained">
            Mute User
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// Separate CommentItem component for better organization
const CommentItem = ({
  comment,
  isAdmin,
  currentUserId,
  onMenuOpen,
  onUnmute,
  bgColors,
  formatTimestamp,
  getInitials,
  theme,
  editingCommentId,
  editedText,
  setEditedText,
  onSaveEdit,
  onCancelEdit,
  replyingToId,
  replyText,
  setReplyText,
  onReplyClick,
  onSubmitReply,
  onCancelReply,
  isMuted,
  onVote,
  userVotes,
  depth = 0
}) => {
  const { data: commentUserData } = useUserData(comment.userId);
  const { data: isUserMuted = false } = useUserMutedStatus(comment.userId);

  // Admins can moderate all comments
  const canModerate = isAdmin;
  const isEditing = editingCommentId === comment.id;
  const isReplying = replyingToId === comment.id;

  // Calculate vote score
  const voteScore = (comment.upvotes || 0) - (comment.downvotes || 0);
  const userVote = userVotes?.[comment.id];
  const hasUpvoted = userVote === 'upvote';
  const hasDownvoted = userVote === 'downvote';

  return (
    <Paper
      sx={{
        p: 2,
        bgcolor: depth > 0 ? alpha(bgColors.input, 0.5) : bgColors.input,
        border: `1px solid ${bgColors.border}`,
        borderLeft: depth > 0 ? `3px solid ${alpha(theme.palette.primary.main, 0.3)}` : `1px solid ${bgColors.border}`,
        borderRadius: 1.5,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: bgColors.hover,
          borderColor: alpha(theme.palette.primary.main, 0.2),
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {/* Vote Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
            pt: 0.5,
          }}
        >
          <Tooltip title={hasUpvoted ? "Remove upvote" : "Upvote"} arrow>
            <IconButton
              size="small"
              onClick={() => onVote(comment.id, 'upvote')}
              sx={{
                color: hasUpvoted ? theme.palette.primary.main : bgColors.text.secondary,
                padding: '4px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <ArrowUpwardIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              color: voteScore > 0
                ? theme.palette.success.main
                : voteScore < 0
                  ? theme.palette.error.main
                  : bgColors.text.secondary,
              minWidth: 24,
              textAlign: 'center',
            }}
          >
            {voteScore > 0 ? `+${voteScore}` : voteScore}
          </Typography>

          <Tooltip title={hasDownvoted ? "Remove downvote" : "Downvote"} arrow>
            <IconButton
              size="small"
              onClick={() => onVote(comment.id, 'downvote')}
              sx={{
                color: hasDownvoted ? theme.palette.error.main : bgColors.text.secondary,
                padding: '4px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                  transform: 'translateY(1px)',
                },
              }}
            >
              <ArrowDownwardIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Comment Content */}
        <Stack spacing={1.5} sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {getInitials(comment.userName)}
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: bgColors.text.primary }}>
                    {comment.userName}
                  </Typography>
                  {isAdmin && comment.userId === currentUserId && (
                    <Chip
                      label="Admin"
                      size="small"
                      icon={<CheckCircleIcon />}
                      sx={{
                        height: 20,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        '& .MuiChip-icon': {
                          fontSize: 14,
                          color: theme.palette.success.main,
                        },
                      }}
                    />
                  )}
                  {isUserMuted && isAdmin && (
                    <Chip
                      label="Muted"
                      size="small"
                      icon={<BlockIcon />}
                      onClick={() => onUnmute(comment.userId)}
                      sx={{
                        height: 20,
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main,
                        cursor: 'pointer',
                        '& .MuiChip-icon': {
                          fontSize: 14,
                          color: theme.palette.error.main,
                        },
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: bgColors.text.secondary }}>
                  {formatTimestamp(comment.createdAt)}
                </Typography>
              </Box>
            </Box>
            {canModerate && (
              <IconButton
                size="small"
                onClick={(e) => onMenuOpen(e, comment)}
                sx={{
                  color: bgColors.text.secondary,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {isEditing ? (
            <Box>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: bgColors.input,
                      '& fieldset': {
                        borderColor: bgColors.border,
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: bgColors.text.primary,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onCancelEdit}
                    sx={{
                      textTransform: 'none',
                      borderColor: bgColors.border,
                      color: bgColors.text.secondary,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onSaveEdit(comment.id)}
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: bgColors.text.primary, lineHeight: 1.6 }}>
              {comment.text}
              {comment.updatedAt && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: bgColors.text.secondary,
                    ml: 1,
                    fontStyle: 'italic',
                  }}
                >
                  (edited)
                </Typography>
              )}
            </Typography>
          )}

          {/* Reply Button */}
          {!isEditing && currentUserId && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                startIcon={<ReplyIcon />}
                onClick={() => onReplyClick(comment.id)}
                sx={{
                  textTransform: 'none',
                  color: bgColors.text.secondary,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Reply
              </Button>
            </Box>
          )}

          {/* Reply Input */}
          {isReplying && (
            <Box sx={{ mt: 2 }}>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  autoFocus
                  disabled={isMuted}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: bgColors.input,
                      '& fieldset': {
                        borderColor: bgColors.border,
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: bgColors.text.primary,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onCancelReply}
                    sx={{
                      textTransform: 'none',
                      borderColor: bgColors.border,
                      color: bgColors.text.secondary,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onSubmitReply(comment.id)}
                    disabled={!replyText.trim() || isMuted}
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    Reply
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Nested Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack spacing={2}>
                {comment.replies.map((reply) => (
                  <CommentItem
                    key={reply.id}
                    comment={reply}
                    isAdmin={isAdmin}
                    currentUserId={currentUserId}
                    onMenuOpen={onMenuOpen}
                    onUnmute={onUnmute}
                    bgColors={bgColors}
                    formatTimestamp={formatTimestamp}
                    getInitials={getInitials}
                    theme={theme}
                    editingCommentId={editingCommentId}
                    editedText={editedText}
                    setEditedText={setEditedText}
                    onSaveEdit={onSaveEdit}
                    onCancelEdit={onCancelEdit}
                    replyingToId={replyingToId}
                    replyText={replyText}
                    setReplyText={setReplyText}
                    onReplyClick={onReplyClick}
                    onSubmitReply={onSubmitReply}
                    onCancelReply={onCancelReply}
                    isMuted={isMuted}
                    onVote={onVote}
                    userVotes={userVotes}
                    depth={depth + 1}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default BankDiscussion;
